import { useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { Check, Zap, Battery, Bolt, Info } from "lucide-react";
import { Button } from "./ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";

const plans = [
  {
    name: "AC Slow",
    description: "Perfect for overnight charging",
    price: "14",
    unit: "per kWh",
    power: "3.3 - 7.2 kW",
    time: "6-8 hours for full charge",
    icon: Battery,
    features: [
      "Ideal for home/office charging",
      "Best for overnight stays",
      "Lowest cost per charge",
      "All vehicle types supported",
    ],
    color: "secondary",
    popular: false,
  },
  {
    name: "DC Fast",
    description: "Quick top-up for busy schedules",
    price: "21",
    unit: "per kWh",
    power: "25 - 60 kW",
    time: "45-90 min for 80% charge",
    icon: Zap,
    features: [
      "Rapid charging technology",
      "Perfect for highway stops",
      "Real-time availability",
      "CCS2 & CHAdeMO support",
      "Priority support",
    ],
    color: "primary",
    popular: true,
  },
  {
    name: "Ultra Fast",
    description: "Premium speed for premium EVs",
    price: "24",
    unit: "per kWh",
    power: "100+ kW",
    time: "20-30 min for 80% charge",
    icon: Bolt,
    features: [
      "Fastest charging available",
      "Premium EV support",
      "Dedicated parking",
      "Complimentary amenities",
      "VIP support line",
    ],
    color: "accent",
    popular: false,
  },
];

const Pricing = () => {
  const containerRef = useRef(null);
  const isInView = useInView(containerRef, { once: true, margin: "-100px" });
  const [hoveredPlan, setHoveredPlan] = useState(null);

  return (
    <section
      id="pricing"
      className="py-24 md:py-32 bg-background relative overflow-hidden"
      data-testid="pricing-section"
    >
      {/* Background */}
      <div className="absolute inset-0 opacity-30">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/10 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[150px]" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 relative z-10" ref={containerRef}>
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">
            Transparent Pricing
          </span>
          <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-foreground mt-4">
            SIMPLE & AFFORDABLE
            <br />
            <span className="text-primary">CHARGING RATES</span>
          </h2>
          <p className="text-foreground-muted text-lg mt-6 max-w-2xl mx-auto">
            No hidden fees, no subscriptions required. Pay only for what you use.
            Transparent pricing that makes EV ownership easy.
          </p>
        </motion.div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              onMouseEnter={() => setHoveredPlan(plan.name)}
              onMouseLeave={() => setHoveredPlan(null)}
              className={`relative glass-card transition-all duration-300 ${
                plan.popular
                  ? "border-primary md:scale-105 md:-my-4"
                  : hoveredPlan === plan.name
                  ? "border-white/30"
                  : ""
              }`}
              data-testid={`pricing-card-${plan.name.toLowerCase().replace(" ", "-")}`}
            >
              {/* Popular Badge */}
              {plan.popular && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-black text-sm font-bold rounded-full">
                  MOST POPULAR
                </div>
              )}

              {/* Icon */}
              <div
                className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 ${
                  plan.color === "primary"
                    ? "bg-primary/10 text-primary"
                    : plan.color === "secondary"
                    ? "bg-secondary/10 text-secondary"
                    : "bg-accent/10 text-accent"
                }`}
              >
                <plan.icon className="w-8 h-8" />
              </div>

              {/* Plan Info */}
              <h3 className="font-heading text-2xl text-foreground">{plan.name}</h3>
              <p className="text-foreground-muted text-sm mt-1">{plan.description}</p>

              {/* Price */}
              <div className="my-6">
                <div className="flex items-baseline gap-1">
                  <span className="text-foreground-muted">Rs</span>
                  <span
                    className={`font-heading text-5xl ${
                      plan.color === "primary"
                        ? "text-primary"
                        : plan.color === "secondary"
                        ? "text-secondary"
                        : "text-accent"
                    }`}
                  >
                    {plan.price}
                  </span>
                  <span className="text-foreground-muted">/{plan.unit}</span>
                </div>
              </div>

              {/* Power & Time */}
              <div className="flex items-center gap-4 py-4 border-y border-white/10">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="flex items-center gap-2 cursor-help">
                        <Zap className="w-4 h-4 text-foreground-muted" />
                        <span className="text-sm text-foreground">{plan.power}</span>
                        <Info className="w-3 h-3 text-foreground-muted" />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Charging power output</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <p className="text-foreground-muted text-sm mt-3">{plan.time}</p>

              {/* Features */}
              <ul className="mt-6 space-y-3">
                {plan.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-3">
                    <Check
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${
                        plan.color === "primary"
                          ? "text-primary"
                          : plan.color === "secondary"
                          ? "text-secondary"
                          : "text-accent"
                      }`}
                    />
                    <span className="text-foreground-muted text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              {/* CTA */}
              <Button
                className={`w-full mt-8 rounded-full font-bold transition-all h-12 ${
                  plan.popular
                    ? "bg-primary text-black hover:shadow-[0_0_20px_rgba(204,255,0,0.5)]"
                    : "bg-white/10 text-foreground hover:bg-white/20"
                }`}
                data-testid={`pricing-cta-${plan.name.toLowerCase().replace(" ", "-")}`}
              >
                Start Charging
              </Button>
            </motion.div>
          ))}
        </div>

        {/* Bottom Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-12 text-center"
        >
          <p className="text-foreground-muted text-sm">
            * Prices are exclusive of GST. Actual charging time depends on vehicle
            battery capacity and state of charge.
          </p>
          <p className="text-foreground-muted text-sm mt-2">
            Have questions?{" "}
            <a href="#faq" className="text-primary hover:underline">
              Check our FAQ
            </a>{" "}
            or{" "}
            <a href="#contact" className="text-primary hover:underline">
              contact us
            </a>
            .
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default Pricing;
